import { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { isInYear } from "./inYear";

const normalizeIban = (v: string | null | undefined) => (v ?? "").replace(/\s+/g, "").toUpperCase();

// Name and purpose matches always need a check; IbanOrName only counts when the IBAN is on the contact
const NEEDS_CHECK_RULES: ReadonlySet<GQL.ITransactionContactMatchRule> = new Set([
    GQL.ITransactionContactMatchRule.NameSubstring,
    GQL.ITransactionContactMatchRule.PurposeLabel,
    GQL.ITransactionContactMatchRule.PurposeIban,
]);

const matchDetail = (
    m: GQL.ITransactionContactMatch,
    ibanBacked: (m: GQL.ITransactionContactMatch) => boolean,
): AgentDetailItem | undefined => {
    const ref = { kind: "bankTransaction" as const, id: String(m.transactionId) };
    const target = m.contactId ? { target: { kind: "contact" as const, id: String(m.contactId) } } : {};
    switch (m.outcome) {
        case GQL.ITransactionContactMatchOutcome.Matched:
            const needsCheck =
                m.rule !== undefined &&
                m.rule !== null &&
                (NEEDS_CHECK_RULES.has(m.rule) ||
                    (m.rule === GQL.ITransactionContactMatchRule.IbanOrName && !ibanBacked(m)));
            if (needsCheck) {
                return {
                    group: "needsYou",
                    ref,
                    ...target,
                    outcome: { textId: "app.agent.details.bankContacts.checkMatch" },
                };
            }
            return { group: "done", ref, ...target, outcome: { textId: "app.agent.details.bankContacts.matched" } };
        case GQL.ITransactionContactMatchOutcome.AlreadyLinked:
            return {
                group: "untouched",
                ref,
                ...target,
                outcome: { textId: "app.agent.details.bankContacts.alreadyLinked" },
            };
        case GQL.ITransactionContactMatchOutcome.NoMatch:
            return { group: "untouched", ref, outcome: { textId: "app.agent.details.bankContacts.noMatch" } };
        case GQL.ITransactionContactMatchOutcome.NotFound:
            return { group: "untouched", ref, outcome: { textId: "app.agent.details.bankContacts.notFound" } };
        default:
            return undefined;
    }
};

// Mirrors the server cap of bankingTransactionsMatchContacts
const MATCH_BATCH = 5000;

export const bankContactsStep: AgentStep = {
    id: "bankContacts",
    product: [GQL.IProductKey.Banks, GQL.IProductKey.Contacts],
    async run(ctx, io) {
        const ids = ctx
            .bankTransactions()
            .filter((tx) => !tx.contactId && isInYear(tx.bookingDate, ctx.year))
            .map((tx) => String(tx.id));
        if (ids.length === 0) return { status: "nothing" };

        const matchApproved = await io.approve({
            textId: "app.agent.bankContacts.approveMatch.question",
            values: { count: ids.length },
            goLabelId: "app.agent.bankContacts.approveMatch.go",
            learnMoreId: "app.agent.bankContacts.learnMoreMatch",
        });
        const matches = matchApproved
            ? await withThinking(io, "app.agent.bankContacts.thinking", async () => {
                  const all: GQL.ITransactionContactMatch[] = [];
                  for (let i = 0; i < ids.length; i += MATCH_BATCH) {
                      all.push(...(await ctx.matchBankContacts(ids.slice(i, i + MATCH_BATCH))));
                  }
                  return all;
              })
            : [];
        if (
            matches.length > 0 &&
            matches.every((m) => m.outcome === GQL.ITransactionContactMatchOutcome.NoContactsProduct)
        ) {
            io.post({ kind: "warning", textId: "app.agent.bankContacts.noContactsProduct" });
            return { status: "skipped" };
        }

        // the last outcome per transaction wins: a create result replaces the match result
        const outcomes = new Map<string, AgentDetailItem>();
        const txById = new Map(ctx.bankTransactions().map((tx) => [String(tx.id), tx]));
        const ibanBacked = (m: GQL.ITransactionContactMatch) => {
            const iban = normalizeIban(txById.get(String(m.transactionId))?.counterpartyIbanOrAccountNum);
            return iban !== "" && !!m.contactId && ctx.contactIbans(String(m.contactId)).includes(iban);
        };
        const matchDetails = matches.flatMap((m): AgentDetailItem[] => {
            const item = matchDetail(m, ibanBacked);
            return item ? [item] : [];
        });
        for (const item of matchDetails) if (item.ref) outcomes.set(item.ref.id, item);

        const matched = matches.filter((m) => m.outcome === GQL.ITransactionContactMatchOutcome.Matched).length;
        const linkedIds = new Set(
            matches
                .filter(
                    (m) =>
                        m.outcome === GQL.ITransactionContactMatchOutcome.Matched ||
                        m.outcome === GQL.ITransactionContactMatchOutcome.AlreadyLinked,
                )
                .map((m) => String(m.transactionId)),
        );
        if (matched > 0) {
            io.post({
                kind: "done",
                textId: "app.agent.bankContacts.matched",
                values: { count: matched },
                details: matchDetails,
            });
        }

        const rest = ids.filter((id) => !linkedIds.has(id));
        const suggestions =
            rest.length > 0
                ? await withThinking(io, "app.agent.bankContacts.suggesting", () =>
                      ctx.readBankContactSuggestions(rest),
                  )
                : [];

        const summary: ReportLine[] = [];
        const open: ReportLine[] = [];
        if (matched > 0) summary.push({ textId: "app.agent.report.bankContactsMatched", values: { count: matched } });
        const toCheck = matchDetails.filter((item) => item.group === "needsYou").length;
        if (toCheck > 0) open.push({ textId: "app.agent.report.bankContactsCheck", values: { count: toCheck } });

        let createApproved = false;
        if (suggestions.length > 0) {
            createApproved = await io.approve({
                textId: "app.agent.bankContacts.approveCreate.question",
                values: { n: rest.length, count: suggestions.length },
                goLabelId: "app.agent.bankContacts.approveCreate.go",
                learnMoreId: "app.agent.bankContacts.learnMoreCreate",
            });
            if (createApproved) {
                const results = await withThinking(io, "app.agent.bankContacts.creating", () =>
                    ctx.createBankContacts(
                        suggestions.map((s) => s.vatId),
                        rest,
                    ),
                );
                const count = (outcome: GQL.IBankContactSuggestionOutcome) =>
                    results.filter((r) => r.outcome === outcome).length;
                const created = count(GQL.IBankContactSuggestionOutcome.CreatedAndLinked);
                const existing = count(GQL.IBankContactSuggestionOutcome.LinkedToExisting);
                const createDetails = results.flatMap((r): AgentDetailItem[] => {
                    const textId =
                        r.outcome === GQL.IBankContactSuggestionOutcome.CreatedAndLinked
                            ? ("app.agent.details.bankContacts.created" as const)
                            : r.outcome === GQL.IBankContactSuggestionOutcome.LinkedToExisting
                              ? ("app.agent.details.bankContacts.linkedExisting" as const)
                              : undefined;
                    if (!textId) return [];
                    return r.linkedTransactionIds.map((id): AgentDetailItem => ({
                        group: "done",
                        ref: { kind: "bankTransaction", id: String(id) },
                        ...(r.contactId ? { target: { kind: "contact" as const, id: String(r.contactId) } } : {}),
                        outcome: { textId },
                    }));
                });
                for (const item of createDetails) if (item.ref) outcomes.set(item.ref.id, item);
                if (created > 0) {
                    io.post({
                        kind: "done",
                        textId: "app.agent.bankContacts.created",
                        values: { count: created },
                        details: createDetails,
                    });
                    summary.push({ textId: "app.agent.report.bankContactsCreated", values: { count: created } });
                }
                if (existing > 0) {
                    summary.push({
                        textId: "app.agent.report.bankContactsLinkedExisting",
                        values: { count: existing },
                    });
                }
            } else if (matchApproved) {
                open.push({ textId: "app.agent.report.bankContactsOpen", values: { count: suggestions.length } });
            }
        }

        if (!matchApproved && !createApproved) return { status: "skipped", byUser: true };
        return {
            status: summary.length > 0 || open.length > 0 ? "done" : "nothing",
            summary,
            open,
            details: ids.flatMap((id): AgentDetailItem[] => {
                const item = outcomes.get(id);
                return item ? [item] : [];
            }),
        };
    },
};
